import { ref } from 'vue';
import { usePayee } from '@/payee.composable';
import { PaymentMethod } from '@/payment-methods/payment-method.model';
import { API } from 'aws-amplify';

const paymentResponse = ref<PaymentResponse>();

export function useBillPay() {
  return {
    payInvoiceForPayor,
    payInvoiceForAnonymous,
    paymentResponse,
  }
}

export interface InvoicePaymentContext {
  invoiceId: string;
  paymentAmount: string;
  paymentMethodId: string | undefined;
  shouldEmailReceipt: boolean;
  paymentMethod?: PaymentMethod;
}

interface PaymentRequest {
  paymentMethodId: string | undefined;
  applyBalance: string;
  emailReceipt: boolean;
  paymentMethod?: PaymentMethod;
  paymentMethodCustomerId?: string;
}

async function payInvoice( request: PaymentRequest, invoiceId: string ) {
  const { payee } = usePayee();
  if (!payee.value) {
    throw new Error('Payee not found');
  }
  const uri = `/v1/payees/${payee.value.id}/invoices/${invoiceId}/paymentRequest`;
  const result = await API.post('API', uri, { body: { ...request } });
  paymentResponse.value = result;
  return result;
}

async function payInvoiceForPayor(context: InvoicePaymentContext) {
  const request: PaymentRequest = {
    paymentMethodId: context.paymentMethodId,
    applyBalance: context.paymentAmount,
    emailReceipt: context.shouldEmailReceipt,
    paymentMethod: context.paymentMethod,
  }
  const { payee } = usePayee();
  if (!payee.value) {
    throw new Error('Payee not found');
  }

  const result = await API.post('API', `/v1/payor/invoices/${context.invoiceId}/paymentRequest`, { body: { ...request } });
  paymentResponse.value = result;
  return result;
}

async function payInvoiceForAnonymous(context: InvoicePaymentContext): Promise<PaymentResponse> {
  const request: PaymentRequest = {
    paymentMethodId: context.paymentMethodId,
    applyBalance: context.paymentAmount,
    emailReceipt: context.shouldEmailReceipt,
    paymentMethod: context.paymentMethod
  };
  return payInvoice(request, context.invoiceId);
}

interface Transaction {
  createdAt: string;
  id: string;
  invoiceId: string;
  isCaptured: number;
  method: string;
  paymentMethodId: string;
  preAuth: boolean;
  success: boolean;
  total: number;
  type: string;
  updatedAt: string;
}

interface PaymentResponse {
  balanceDue: number;
  customerId: string;
  invoiceId: string;
  paidAt: string; // not there
  paymentAttemptFailed: boolean;
  status: string;
  total: number;
  totalPaid: number;
  childTransactions?: Transaction[];
}
